<script setup lang="ts">
import { PartialDisplayType, TrustFAQItemType } from '@/builder/enums';
import { Tooltip } from 'floating-vue';
import { PropType, Ref, computed, inject, onBeforeMount, ref } from 'vue';
import { IPage, PageDesign } from '../page/PageInterface';

//props
const props = defineProps({
    page: {
        type: Object as PropType<IPage>,
        required: true
    },
    builderMode: {
        type: Boolean,
        required: false,
        default: false
    },
    tooltipContainer: {
        type: String,
        required: false,
        default: '.trust-center-container'
    }
});

//injects
const pageDesign = inject("pageDesign") as Ref<PageDesign>;

//refs
const activeTooltipIndex = ref(-1);
const isMobile = ref(false);
const currentAccordion = ref([]) as any;

//computed
const trustCenter = computed(() => {
    return props.page.trustFAQCenter;
});

const showTrustCenter = computed(() => {
    return props.page.isTrustFAQCenterEnabled && trustCenter.value && trustCenter.value.trustFAQItems.length > 0;
});

const isFormDisplay = computed(() => {
    return props.page.partialDisplayType === PartialDisplayType.Embed && 'form-display';
})

const getTextColor = computed(() => {
    let textColor;

    if (pageDesign.value.trustFaqTextColor) {
        textColor = pageDesign.value.trustFaqTextColor;
    } else if (pageDesign.value.linkColor) {
        textColor = pageDesign.value.linkColor;
    } else {
        textColor = '#A9B0B4';
    }
    return textColor
});

const getBottomPadding = computed(() => {
    return props.page.partialDisplayType === PartialDisplayType.Modal ? '20px' : '0';
});

const getTopPadding = computed(() => {
    return props.page.partialDisplayType === PartialDisplayType.Modal ? '20px' : '40px';
});

const updateIsMobile = () => {
    isMobile.value = window.innerWidth < 768;
};

//methods
const showToolTip = (index: number) => {
    return activeTooltipIndex.value === index;
};

const hoverTooltip = (index: number, show: boolean) => {
    if (show) {
        activeTooltipIndex.value = index;
    } else {
        activeTooltipIndex.value = -1;
    }
};

// Accordion toggling method
const toggleAccordion = (index: any) => {
    if (currentAccordion.value.includes(index)) {
        currentAccordion.value.length == 1 ?
            currentAccordion.value = [] :
            currentAccordion.value.splice(index, 1);
    }
    else {
        currentAccordion.value.push(index);
    }
}

// Accordion animation methods
const beforeEnter = (el) => {
    el.style.height = '0';
};

const enter = (el) => {
    el.style.height = el.scrollHeight + 'px';
};

const beforeLeave = (el) => {
    el.style.height = el.scrollHeight + 'px';
}

const leave = (el) => {
    el.style.height = '0';
}

// Mobile resizing event
window.addEventListener("resize", updateIsMobile);

// onBeforeMount event
onBeforeMount(() => {
    updateIsMobile();
});
</script>

<template>
    <section :class="['trust-center-container', isFormDisplay]">
        <template v-if="showTrustCenter">
            <ul>
                <li v-for="(item, index) in trustCenter.trustFAQItems" :key="item.id">
                    <template v-if="isMobile">
                        <template v-if="item.type === TrustFAQItemType.HoverText">
                            <button type="button" class="trust-center-accordion"
                                :aria-expanded="currentAccordion.includes(index)"
                                :aria-controls="`trustCenterContent${index + 1}`" @click="toggleAccordion(index)">
                                <template v-if="index === 0">
                                    <i :class="`fa-solid fa-${trustCenter.icon} trust-center-icon`"
                                        aria-hidden="true"></i>
                                </template>
                                {{ item.title }}
                            </button>

                            <Transition appear @before-enter="beforeEnter" @enter="enter" @before-leave="beforeLeave"
                                @leave="leave">
                                <div v-if="currentAccordion.includes(index)" :id="`trustCenterContent${index + 1}`"
                                    class="slide-down rd-overflow-hidden">
                                    <p class="trust-center-content">{{ item.content }}</p>
                                </div>
                            </Transition>
                        </template>

                        <template v-else>
                            <div>
                                <template v-if="index === 0">
                                    <i :class="`fa-solid fa-${trustCenter.icon} trust-icon`" aria-hidden="true"></i>
                                </template>

                                <a :href="item.content ?? undefined" target="_blank" class="trust-center-item">
                                    {{ item.title }}
                                </a>
                            </div>
                        </template>
                    </template>

                    <template v-else>
                        <div class="trust-center-tooltip">
                            <!-- if first item add icon -->
                            <template v-if="index === 0">
                                <i :class="`fa-solid fa-${trustCenter.icon} trust-center-icon`" aria-hidden="true"></i>
                            </template>

                            <template v-if="item.type === TrustFAQItemType.HoverText">
                                <Tooltip distance="4" :triggers="['hover']" :container="tooltipContainer" delay="15"
                                    :show="showToolTip(index)" @mouseenter="hoverTooltip(index, true)"
                                    @mouseleave="hoverTooltip(index, false)" popperClass="tooltip-light">
                                    <span class="trust-center-tooltip-text">{{ item.title }}</span>
                                    <template #popper>
                                        {{ item.content }}
                                    </template>
                                </Tooltip>
                            </template>

                            <template v-else>
                                <a :href="item.content ?? undefined" target="_blank" class="trust-center-item">
                                    {{ item.title }}
                                </a>
                            </template>
                        </div>
                    </template>
                </li>
            </ul>
        </template>

        <div v-else class="secure-donation">
            <!-- show secure icon and text Secure Donation -->
            <i class="fa-solid fa-shield-heart secure-donation-icon" aria-hidden="true"></i>
            <span class="secure-donation-title">Secure Donation</span>
        </div>
    </section>
</template>

<style lang="scss" scoped>
.trust-center-container {
    padding-bottom: 1.75rem;
    padding-top: 1.75rem;
}

.trust-center-title,
.trust-center-accordion,
.trust-center-item,
.trust-center-icon,
.trust-center-tooltip-text,
.secure-donation-title,
.secure-donation-icon {
    color: v-bind(getTextColor) !important;
}

.trust-center-item,
.trust-center-tooltip {
    &::before {
        background-color: v-bind(getTextColor);
    }
}

@media(min-width: 768px) {
    .trust-center-container {
        padding-bottom: v-bind(getBottomPadding);
        padding-top: v-bind(getTopPadding);
    }
}
</style>
